import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Hourglass, Loader2, Mail, Pencil, UserPlus, Users, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import type { TeamEntriesBlock, TeamEntry } from '../../services/team-entries';

interface TeamEntriesCardProps {
  teamName: string;
  /** This squad's entries here. */
  entries: TeamEntry[];
  loading: boolean;
  loadFailed: boolean;
  /** Whether the squad may write right now. */
  open: boolean;
  blockedReason: TeamEntriesBlock | null;
  /** Absent while the squad's id is not known yet (the list is loading). */
  onAdd?: () => void;
  onEdit: (entry: TeamEntry) => void;
  onWithdraw: (entry: TeamEntry) => void;
}

function formatDate(value?: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * One squad a team manager speaks for at this competition, and the way to put
 * more of it on the start list. Drawn once per squad by TeamEntriesSection,
 * which holds the list, the dialogs and the URL; this is only the card.
 *
 * Every entry of the squad is shown, the desk's and the athletes' own included,
 * with its category and the turn booked — or that a turn is still to choose —
 * because "who of ours is in, and when" is the question; only the rows a team manager
 * made can be changed from here, and only while entries are open. When they are
 * not, the card says why — in words that name the organiser's missing agreement
 * only to whoever works the desk.
 *
 * A squad is twenty or thirty names, and the rounds below are where a team
 * manager works on the day itself, so the list starts folded: the header still
 * says how many are in, and still offers to enter one more.
 */
export default function TeamEntriesCard({
  teamName,
  entries,
  loading,
  loadFailed,
  open,
  blockedReason,
  onAdd,
  onEdit,
  onWithdraw,
}: TeamEntriesCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Said once, above the list, rather than on every row the squad did not make.
  const othersEntered = open && entries.some((entry) => !entry.team_entry);

  return (
    <Card data-testid="team-entries-card" data-team={teamName}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            className="flex min-w-0 items-start gap-1.5 text-left"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            data-testid="team-entries-toggle"
          >
            <ChevronDown
              className={`mt-0.5 size-4 shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`}
              aria-hidden
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold">{teamName}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {t('teamEntries.title')}
                {!loading && !loadFailed && ` · ${t('teamEntries.count', { count: entries.length })}`}
              </span>
            </span>
          </button>
          {open && onAdd && (
            <Button size="sm" className="shrink-0 gap-2" onClick={onAdd} data-testid="team-entries-add">
              <UserPlus className="size-4" />
              {t('teamEntries.add')}
            </Button>
          )}
        </div>

        {!open && blockedReason && (
          <p
            className="flex items-start gap-1.5 text-xs text-muted-foreground"
            data-testid="team-entries-blocked"
            data-reason={blockedReason}
          >
            <Hourglass className="mt-px size-3 shrink-0" aria-hidden />
            {t(`teamEntries.blocked.${blockedReason}`)}
          </p>
        )}

        {expanded && othersEntered && (
          <p className="text-xs text-muted-foreground" data-testid="team-entries-only-yours">
            {t('teamEntries.onlyYours')}
          </p>
        )}

        {!expanded ? null : loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : loadFailed ? (
          <p className="text-sm text-destructive">{t('teamEntries.loadError')}</p>
        ) : entries.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground" data-testid="team-entries-empty">
            <Users className="mx-auto mb-2 size-8 opacity-50" />
            {t('teamEntries.empty')}
          </div>
        ) : (
          <ul className="divide-y" data-testid="team-entries-list">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-center gap-2 py-2" data-testid="team-entry">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{entry.full_name}</p>
                  <p className="truncate text-xs text-muted-foreground" data-testid="team-entry-details">
                    {[
                      formatDate(entry.birth_date),
                      entry.category_name ?? t('teamEntries.unsorted'),
                      entry.competition_round_name,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {/* The class is split over turns and none is booked: on a
                        row of the squad's own, the pencil books one. */}
                    {entry.turn_to_choose && (
                      <span className="text-amber-700 dark:text-amber-400" data-testid="team-entry-turn-to-choose">
                        {' · '}
                        {t('stageEntry.turnChoice.pending')}
                      </span>
                    )}
                  </p>
                </div>
                {entry.invited_at && (
                  <Mail
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-label={t('teamEntries.invited')}
                    data-testid="team-entry-invited"
                  />
                )}
                {entry.confirmed && (
                  <Badge variant="secondary" className="shrink-0">
                    {t('teamEntries.confirmed')}
                  </Badge>
                )}
                {entry.editable && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0"
                      aria-label={t('teamEntries.edit', { name: entry.full_name })}
                      onClick={() => onEdit(entry)}
                      data-testid="team-entry-edit"
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0"
                      aria-label={t('teamEntries.withdraw', { name: entry.full_name })}
                      onClick={() => onWithdraw(entry)}
                      data-testid="team-entry-withdraw"
                    >
                      <X className="size-4 text-destructive" />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
